<script>
import Layout from '@layouts/main.vue'
import { mapActions, mapGetters } from 'vuex'
import {
  getWeekRangeStringFromTimesheet,
  mapPendingTimesheets,
} from '@/helpers/timesheet-helpers'
import TimesheetWeekGroup from '@/components/timesheet/timesheet-week-group.vue'
import TimesheetChangeTimeCard from '@/components/timesheet/timesheet-change-time-card.vue'
import { TimesheetOperationType } from '@/shared/constants/timesheet/TimesheetOperationType'
import FixedActionFooter from '@/components/layout/fixed-action-footer.vue'

export default {
  name: 'TimesheetsView',
  metaInfo() {
    return {
      title: this.$route?.meta?.title,
      meta: [{ name: 'description', content: 'The Timesheets page.' }],
    }
  },
  components: {
    Layout,
    TimesheetWeekGroup,
    TimesheetChangeTimeCard,
    FixedActionFooter,
  },
  data: () => {
    return {
      pendingApproval: [],
      approvedCount: 0,
      pendingReviewCount: 0,
      selectedCount: 0,
      pendingApprovalCount: 0,
      hasLoadedSuccessfully: false,
      masterCheckboxState: false,
      selectAll: false,
      showChangeDialog: false,
      changeTimeTimesheet: null,
      keyOfChangeTimeTimesheet: null,
      selectedTimesheets: [],
      TimesheetOperationType,
      modifiedCount: 0,
      removedCount: 0,
    }
  },
  computed: {
    ...mapGetters({
      isLoadingCRUD: 'timesheets/isLoadingCRUD',
    }),
    masterCheckboxIndeterminate() {
      return (
        this.selectedCount > 0 && this.selectedCount < this.pendingApprovalCount
      )
    },
    hasSelection() {
      return this.selectedCount > 0 || this.selectAll
    },
    submissionBreakdownText() {
      return this.removedCount && this.modifiedCount
        ? `(${this.removedCount} ${this.$t('timesheets.removedText')}, ${
            this.modifiedCount
          } ${this.$t('timesheets.modifiedText')})`
        : this.removedCount
        ? `(${this.removedCount} ${this.$t('timesheets.removedText')})`
        : this.modifiedCount
        ? `(${this.modifiedCount} ${this.$t('timesheets.modifiedText')})`
        : ''
    },
  },
  async created() {
    const result = await this.loadTimesheets()

    if (!result.isSuccess) return false

    // extend object to keep track of edits
    const editableTimesheets = result.data.pendingApprovalList.map((ts) => {
      return {
        ...ts,
        newStartTimeLocal: null,
        newEndTimeLocal: null,
        newPayOptionType: null,
        newBreakMinutes: null,
        operationType: TimesheetOperationType.ACCEPT,
      }
    })

    this.initPendingApprovalList(editableTimesheets)
    this.hasLoadedSuccessfully = true
    this.approvedCount = result.data.approvedCount
    this.pendingReviewCount = result.data.pendingReviewCount
    this.pendingApprovalCount = result.data.pendingApprovalList.length
  },
  methods: {
    ...mapActions({
      loadTimesheets: 'timesheets/loadTimesheets',
      modifyTimes: 'timesheets/updateTimes',
      cancelTimesheet: 'timesheets/cancelTimesheet',
      submitTimesheetsForApproval: 'timesheets/submitTimesheetsForApproval',
    }),
    initPendingApprovalList(timesheets) {
      this.pendingApproval = mapPendingTimesheets(timesheets, this.$i18n)
    },
    timesheetsUpdated(timesheets) {
      const index = this.selectedTimesheets.findIndex(
        (x) => x.key === timesheets.key
      )

      if (index >= 0) this.selectedTimesheets[index] = timesheets
      else this.selectedTimesheets.push(timesheets)

      this.selectedCount = this.countSelectedTimesheets(this.selectedTimesheets)
      this.modifiedCount = this.countModifiedTimesheets(this.selectedTimesheets)
      this.removedCount = this.countRemovedTimesheets(this.selectedTimesheets)

      if (this.selectedCount < this.pendingApprovalCount) {
        this.masterCheckboxState = false
      } else {
        this.masterCheckboxState = true
      }
    },
    countSelectedTimesheets(timesheets) {
      if (timesheets.length === 0) return 0

      const count = timesheets
        .map((x) => x.timesheets.length)
        .reduce((a, b) => a + b, 0)

      return count || 0
    },
    countModifiedTimesheets() {
      if (this.selectedTimesheets.length === 0) return 0

      const count = this.selectedTimesheets
        .flatMap((timesheetGroup) => timesheetGroup.timesheets)
        .filter(
          (timesheet) =>
            timesheet.operationType === TimesheetOperationType.MODIFY
        ).length

      return count || 0
    },
    countRemovedTimesheets() {
      if (this.selectedTimesheets.length === 0) return 0

      const count = this.selectedTimesheets
        .flatMap((timesheetGroup) => timesheetGroup.timesheets)
        .filter(
          (timesheet) =>
            timesheet.operationType === TimesheetOperationType.CANCEL
        ).length

      return count || 0
    },
    changeTime(timesheetData) {
      this.changeTimeTimesheet = timesheetData.timesheet
      this.keyOfChangeTimeTimesheet = timesheetData.key
      this.showChangeDialog = true
    },
    updateTimes(updatedTimesheet) {
      // find right group
      const groupIndex = this.selectedTimesheets.findIndex(
        (x) => x.key === this.keyOfChangeTimeTimesheet
      )

      if (groupIndex >= 0) {
        // duplicate selection check
        const index = this.selectedTimesheets[groupIndex].timesheets.findIndex(
          (x) => x.bookingId === updatedTimesheet.bookingId
        )

        if (index < 0) {
          this.selectedTimesheets[groupIndex].timesheets.push(updatedTimesheet)
        }
      }
    },
    async submitTimesheets() {
      let flatSelectedTimesheets = []
      for (const subTimesheets of this.selectedTimesheets) {
        flatSelectedTimesheets = flatSelectedTimesheets.concat(
          subTimesheets.timesheets
        )
      }

      const payload = flatSelectedTimesheets.map((sts) => {
        return {
          bookingId: sts.bookingId,
          locationId: sts.locationId,
          candidateId: sts.candidateId,
          operationType: sts.operationType,
          startTimeLocal: sts.newStartTimeLocal,
          endTimeLocal: sts.newEndTimeLocal,
          breakMinutes: sts.newBreakMinutes,
          payOptionType: sts.newPayOptionType,
        }
      })

      const result = await this.submitTimesheetsForApproval(payload)

      if (result.isSuccess) {
        // update count
        for (var processedItem of flatSelectedTimesheets) {
          if (processedItem.operationType === TimesheetOperationType.ACCEPT)
            this.approvedCount++
          else this.pendingReviewCount++
          this.pendingApprovalCount--

          // remove processed items
          for (var group of this.pendingApproval) {
            if (
              getWeekRangeStringFromTimesheet(processedItem, this.$i18n) ===
              group.title
            ) {
              if (
                Object.prototype.hasOwnProperty.call(
                  group.timesheets,
                  processedItem.candidateId
                )
              ) {
                const index = group.timesheets[
                  processedItem.candidateId
                ].findIndex((x) => x.bookingId === processedItem.bookingId)
                if (index >= 0) {
                  group.timesheets[processedItem.candidateId].splice(index, 1)

                  if (
                    group.timesheets[processedItem.candidateId].length === 0
                  ) {
                    delete group.timesheets[processedItem.candidateId]
                  }
                }
              }
              break
            }
          }
        }

        // clear selection
        for (var subSelectedTimesheets of this.selectedTimesheets) {
          subSelectedTimesheets.timesheets.splice(0)
        }
        this.selectedCount = this.countSelectedTimesheets(
          this.selectedTimesheets
        )

        this.modifiedCount = 0
        this.removedCount = 0
      }
    },
    async masterCheckboxClicked() {
      // modified to select all only when clicked
      if (this.masterCheckboxState && !this.selectAll) {
        // completely unselected state -> select all
        this.selectAll = true
      } else if (this.masterCheckboxState && this.selectAll) {
        // partially selected state -> select all
        this.selectAll = false
        await this.$nextTick()
        this.selectAll = true
      } else if (!this.masterCheckboxIndeterminate) {
        // all selected state -> deselect all
        this.masterCheckboxState = false
        this.selectAll = false
      }
    },
  },
}
</script>

<template>
  <Layout>
    <div class="mb-6 d-flex align-center justify-space-between">
      <BasePageTitle
        :title="$t('timesheets.timesheetPageTitle')"
        :subtitle="$t('timesheets.timesheetPageSubtitle')"
      />

      <v-checkbox
        v-if="pendingApprovalCount > 1"
        v-model="masterCheckboxState"
        :indeterminate="masterCheckboxIndeterminate"
        :label="$t('timesheets.selectAllText')"
        @click="masterCheckboxClicked"
      ></v-checkbox>
    </div>
    <v-skeleton-loader
      v-if="!hasLoadedSuccessfully"
      loading
      type="week-timesheet-group@2"
      :types="{
        'week-timesheet-group': 'week-heading, timesheet-card@2',
        'week-heading': 'heading',
        'timesheet-card': 'avatar-text, timesheet-table',
        'avatar-text': 'avatar, text',
        'timesheet-table':
          'table-thead, divider, table-row, divider, table-row',
      }"
      class="timesheet-skeleton-list"
    ></v-skeleton-loader>
    <section v-else>
      <TimesheetWeekGroup
        v-for="group of pendingApproval"
        :key="group.title"
        :group="group"
        :select-all="selectAll"
        @timesheetsUpdated="timesheetsUpdated"
        @changeTime="changeTime"
      />
    </section>
    <FixedActionFooter>
      <transition name="slide-fade" mode="out-in">
        <section
          v-if="hasSelection"
          key="has-selection"
          class="ml-auto d-flex align-center"
        >
          <span class="blue--text mr-4">
            {{
              $tc('timesheets.submittingText', selectedCount, {
                count: selectedCount,
              })
            }}
            {{ submissionBreakdownText }}
          </span>
          <v-btn
            :disabled="!hasSelection"
            color="blue"
            class="cl-elevation white--text"
            :loading="isLoadingCRUD"
            @click="submitTimesheets"
          >
            <v-icon left>mdi-calendar</v-icon>
            {{ $t('general.submitBtnText') }}
          </v-btn>
        </section>
        <section
          v-else-if="hasLoadedSuccessfully"
          key="no-selection"
          class="ml-auto d-flex align-center"
        >
          <span class="purple--text mr-4">
            {{ approvedCount }}
            {{ $t('timesheets.timesheetApprovedCardTitle') }}
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="purple--text" v-bind="attrs" v-on="on">
                  mdi-help-circle
                </v-icon>
              </template>
              <span>
                {{ $t('timesheets.timesheetApprovedTooltipText') }}
              </span>
            </v-tooltip>
          </span>

          <span class="blue--text">
            {{ pendingReviewCount }}
            {{ $t('timesheets.timesheetPendingReviewCardTitle') }}
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="blue--text" v-bind="attrs" v-on="on">
                  mdi-help-circle
                </v-icon>
              </template>
              <span>
                {{ $t('timesheets.timesheetPendingReviewTooltipText') }}
              </span>
            </v-tooltip>
          </span>
        </section>
      </transition>
    </FixedActionFooter>
    <v-dialog
      v-if="showChangeDialog"
      v-model="showChangeDialog"
      max-width="400px"
    >
      <TimesheetChangeTimeCard
        :timesheet="changeTimeTimesheet"
        @closeModal="showChangeDialog = !showChangeDialog"
        @saveChanges="updateTimes"
      />
    </v-dialog>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@styles';
::v-deep.timesheet-skeleton-list.v-skeleton-loader--is-loading {
  overflow: unset;

  .v-skeleton-loader__week-timesheet-group {
    margin-bottom: 48px;
    overflow: unset;
  }

  .v-skeleton-loader__week-heading .v-skeleton-loader__heading {
    width: 280px;
    margin-bottom: 16px;
  }

  .v-skeleton-loader__timesheet-card {
    margin-bottom: 16px;
    background-color: #fff;
    border-color: #fff;
    border-radius: 24px;
    -webkit-box-shadow: rgb(0 0 0 / 8%) 0 4px 12px;
    box-shadow: rgb(0 0 0 / 8%) 0 4px 12px;
  }

  .v-skeleton-loader__avatar-text {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 16px;

    .v-skeleton-loader__avatar {
      width: 42px;
      height: 42px;
      margin-right: 20px;
    }
    .v-skeleton-loader__text {
      flex: 0 1 auto;
      width: 150px;
      height: 16px;
      margin-left: 12px;
    }
  }

  .v-skeleton-loader__timesheet-table {
    .v-skeleton-loader__heading {
      max-width: 10%;
      height: 16px;
      padding: 0 16px;
    }

    .v-skeleton-loader__table-cell {
      padding: 0 16px;
    }

    .v-skeleton-loader__table-cell:first-child .v-skeleton-loader__text {
      flex: 0 1 auto;
      width: 24px;
      height: 24px;
    }
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.1s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
